import { useEffect } from "react";
import { Outlet, useLocation, useMatch } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { baseRateWarnings, fxWarning } from "./model";
import { useAccount } from "../../app/session";
import { PortalLayout } from "../../components/shell/PortalLayout";
import { today as heuteLokal } from "../../lib/format";
import { setManifest } from "../../pwa";
import { erlaubt, istBetrieb, istEntwicklung, KONSOLE_SICHERHEIT, konsoleBottom, konsoleNav, konsolePalette, ROLLEN_NAME, type KonsolenRolle } from "./konsole-nav";
import { konsoleSuche } from "./konsole-suche";
import { useKonsolenRolle } from "./rolle";
import "./betrieb.css";

/**
 * Rahmen der Betreiber-Konsole. Vertrieb-Konsole (Inhaber 02.11.2026): im Portal-Design (PortalLayout) mit linker
 * Seitenleiste nach Gruppen (Start, Vertrieb, Kunden, Support, Marketing, Technik, Team), am Handy untere Leiste und „Mehr“,
 * Suche über Konten, Leads und Tickets (⌘K). Was angeboten wird, hängt an der Rolle (Admin oder Vertrieb); die Datenbank
 * prüft jede Abfrage selbst. Abmelden führt zurück zur internen Anmeldung (K-4); eigene Sitzung, eigene App „CreatorOffice Intern“.
 */
export function BetriebRahmen() {
  const { pathname } = useLocation();
  const detail = useMatch("/intern/agenturen/:orgId");
  const { account } = useAccount();
  const qc = useQueryClient();
  const rolle: KonsolenRolle = useKonsolenRolle();
  // Eigene App „CreatorOffice Intern“ (Inhaber 24.10. und 03.11.2026): Manifest, Symbol und Name für „Zum Dock hinzufügen“
  useEffect(() => {
    setManifest("/intern");
    return () => setManifest(window.location.pathname);
  }, []);
  const admin = istBetrieb(rolle);
  const entwicklung = istEntwicklung(rolle);
  // Wechselkurse (0076) und Basiszinssätze (SICHT-OEFFENTLICH-BETRIEB-19): Hinweise für die Technik, nur für die Entwicklung (0500)
  const fx = useQuery({ queryKey: ["fx_rates", "letzter", "betrieb"], staleTime: 60 * 60_000, enabled: entwicklung,
    queryFn: () => api.select<{ valid_on: string }>("fx_rates", { columns: "valid_on", order: [["valid_on", "desc"]], limit: 1 }) });
  const baseRates = useQuery({ queryKey: ["platform_base_rates"], enabled: entwicklung,
    queryFn: () => api.select<{ country: string; valid_from: string }>("platform_base_rates", { order: [["country", "asc"], ["valid_from", "desc"]] }) });
  const rateRules = useQuery({ queryKey: ["platform_interest_rules"], enabled: entwicklung,
    queryFn: () => api.select<{ country: string; mode: string }>("platform_interest_rules", { order: [["country", "asc"]] }) });
  const today = heuteLokal();   // nicht UTC (Gesamtprüfung 07.10.2026)
  const fxText = entwicklung && fx.data ? fxWarning(fx.data[0]?.valid_on ?? null, today) : null;
  const rateTexts = entwicklung && baseRates.data && rateRules.data ? baseRateWarnings(rateRules.data, baseRates.data, today) : [];

  async function signOut() {
    try {
      await api.signOut();
    } finally {
      // K-4: in der Dock-App wieder die interne Anmeldung (eigene Sitzung; das Kundenportal bleibt angemeldet)
      window.location.assign("/intern/anmelden");
    }
  }
  const name = account?.platformName?.trim() || account?.session.email || "";
  const kuerzel = (account?.initials ?? name.split(/[\s.@]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("")) || "CO";
  const gesperrt = !erlaubt(rolle, pathname);

  return (
    <PortalLayout
      brandHref="/intern"
      orgName={`Konsole · ${ROLLEN_NAME[rolle]}`}
      nav={konsoleNav(rolle, detail ? detail.pathname : null)}
      bottom={konsoleBottom(rolle)}
      palette={konsolePalette(rolle)}
      suche={(q) => konsoleSuche(qc, api, rolle, q)}
      account={{ name, initials: kuerzel, href: KONSOLE_SICHERHEIT, profileLabel: "Anmeldung & Sicherheit", org: ROLLEN_NAME[rolle] }}
      onSignOut={() => void signOut()}
    >
      <main id="main" className="content tk-betrieb tk-konsole">
        <p className="tk-betrieb-protokoll">{admin ? `${ROLLEN_NAME[rolle]} · Zugriffe des Betreibers und der KI werden protokolliert` : "Vertrieb · Zugriffe werden protokolliert"}</p>
        {fxText && <div className="notice warning" role="alert">{fxText}</div>}
        {rateTexts.map((x) => <div key={x} className="notice warning" role="alert">{x}</div>)}
        {gesperrt ? (
          <div className="panel stack tk-empty" role="alert">
            <h1>{admin ? "Diese Seite ist nur für die Entwicklung freigegeben." : "Diese Seite ist für den Vertrieb nicht freigegeben."}</h1>
            <p>{admin
              ? "Technik, KI-Protokoll, Rechtstexte, KI-Zugänge und Basiszins pflegt die Entwicklung. Die Datenbank lehnt solche Zugriffe für dieses Konto ohnehin ab."
              : "Tarif, Sperren, Support-Zugang, Rabattcodes, KI-Zugänge, Rechtstexte, Technik und Team bleiben beim Betrieb. Die Datenbank lehnt solche Zugriffe für dieses Konto ohnehin ab."}</p>
          </div>
        ) : <Outlet />}
      </main>
    </PortalLayout>
  );
}
